/** Billed-months accounting for PME bundles (parcours étranger).
 *
 *  Front-end mirror of the backend `Payments::ComputeContractPrice#pme_price`
 *  (smerra.back) so the UI can show the billed duration before the quote returns:
 *  count the full elapsed months between the two inclusive dates, then prorate the
 *  trailing days — ≤ 15 leftover days bill as a half-month, > 15 as a full extra
 *  month. The returned value can therefore be fractional (e.g. 2.5). */

import { addMonthsInclusive } from './SubscriptionFlowDefinition'

const MS_PER_DAY = 86400000

/** Largest N such that `start + N months - 1 day <= end` (backend `count_full_months`). */
function countFullMonths(start: Date, end: Date): number {
  const target = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1)
  let months = (target.getFullYear() - start.getFullYear()) * 12 + (target.getMonth() - start.getMonth())
  if (target.getDate() < start.getDate()) months -= 1
  return Math.max(months, 0)
}

/** Days left over once the full months are removed (backend `remaining_days`).
 *  `addMonthsInclusive(start, fullMonths)` is the inclusive last day of those
 *  months, i.e. `start + fullMonths.months - 1.day` clamped to month length. */
function remainingDays(start: Date, end: Date, fullMonths: number): number {
  const fullMonthsEnd = addMonthsInclusive(start, fullMonths)
  const days = Math.round((end.getTime() - fullMonthsEnd.getTime()) / MS_PER_DAY)
  return Math.max(days, 0)
}

/** Billed months between two inclusive dates (full months + prorated remainder). */
export function pmeBilledMonths(start: Date, end: Date): number {
  const fullMonths = countFullMonths(start, end)
  const remaining = remainingDays(start, end, fullMonths)
  if (remaining <= 0) return fullMonths
  return fullMonths + (remaining <= 15 ? 0.5 : 1)
}

/** Localized billed-months number for display ("2,5" in FR, "2.5" in EN). */
export function formatBilledMonths(value: number, locale: string): string {
  return value.toLocaleString(locale, { maximumFractionDigits: 1 })
}

/** Minimal shape of vue-i18n's `t`, so this module stays free of the framework. */
type TranslateFn = (key: string, named: Record<string, unknown>) => string

/**
 * Stay length as every PME screen words it: a day count followed by the billed months in
 * brackets, e.g. « 11 jours (0,5 mois facturé) ».
 *
 * It lives here, next to the billing maths it describes, because two screens show it — the
 * journey's date step and the quote recap — and they used to carry a copy each. The copies
 * were identical until SI-1452 moved the day unit out of the sentence and into its own
 * fragment, updating one caller; the recap then rendered « 11 (0,5 mois facturé) », a number
 * with no unit at all (SI-1473). One function is what stops that happening again.
 *
 * Each half is pluralised on its own count, and independently: a stay can be short in days
 * yet bill a whole month, and vice versa. Months turn plural only from 2 up, so
 * « 1 mois facturé » and « 0,5 mois facturé » are both singular.
 */
export function formatStayDuration(
  stayDays: number | null,
  billedMonths: number | null,
  t: TranslateFn,
  locale: string,
): string {
  if (!stayDays || billedMonths == null) return ''

  const days = t(
    stayDays < 2 ? 'subscription.summary.stayDaysSingular' : 'subscription.summary.stayDays',
    { count: stayDays },
  )

  return t(
    billedMonths < 2
      ? 'subscription.summary.stayDurationBilledSingular'
      : 'subscription.summary.stayDurationBilled',
    { days, months: formatBilledMonths(billedMonths, locale) },
  )
}
